import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../utils/currencyFormatter';
import { PlusIcon, TrashIcon, SearchIcon, MapPinIcon, BookOpenIcon, XIcon, InfoIcon, FileTextIcon, FileIcon, FilePlusIcon } from 'lucide-react';
import { getDefaultCurrency } from '../../utils/appSettings';
import { FileUpload } from '../FileUpload';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
export function RequisitionForm({
  onSave,
  locale = 'en-US'
}) {
  // Generate a system requisition number
  const generateRequisitionNumber = () => {
    const currentYear = new Date().getFullYear();
    const randomDigits = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
    return `REQ-${currentYear}-${randomDigits}`;
  };
  const [formData, setFormData] = useState({
    name: generateRequisitionNumber(),
    vendor: '',
    vendorId: '',
    purchaseJustification: '',
    vendorJustification: '',
    businessCase: '',
    urgencyLevel: 'normal',
    items: [{
      description: '',
      quantity: 1,
      unitPrice: 0,
      type: 'Item',
      uom: 'EA',
      taxCode: 'STD',
      taxRate: 10,
      taxAmount: 0,
      catalogItemId: '',
      glCode: '',
      itemCode: '' // Added to match PO form
    }],
    notes: '',
    noteVisibility: 'Internal' as NoteVisibility,
    noteVisibilityDetails: '',
    deliveryDate: '',
    deliveryAddress: {
      type: 'entity',
      addressId: '',
      customAddress: {
        name: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      }
    },
    currency: getDefaultCurrency().code,
    documents: [] // Add documents array to store uploaded files with document types
  });
  // Document types for the dropdown
  const documentTypes = ['Quote', 'Invoice', 'Delivery Docket', 'Specification Sheet', 'Contract', 'Certificate', 'Compliance Document', 'Technical Drawing', 'Warranty Information', 'Other'];
  // Add errors state
  const [errors, setErrors] = useState({
    lineItems: []
  });
  // Sample vendors for the dropdown
  const [vendors, setVendors] = useState([{
    id: 'V001',
    name: 'Office Depot',
    country: 'United States',
    hasCatalog: true
  }, {
    id: 'V002',
    name: 'Dell Technologies',
    country: 'United States',
    hasCatalog: true
  }, {
    id: 'V003',
    name: 'PrintWorks Inc.',
    country: 'Canada',
    hasCatalog: false
  }, {
    id: 'V004',
    name: 'Server Solutions Inc.',
    country: 'United Kingdom',
    hasCatalog: true
  }, {
    id: 'V005',
    name: 'Global Supplies Co.',
    country: 'Australia',
    hasCatalog: false
  }]);
  // Sample catalog items for vendors
  const [vendorCatalogs, setVendorCatalogs] = useState({
    V001: [{
      id: 'CAT001-001',
      name: 'Premium Paper - A4',
      description: 'Premium quality A4 paper, 500 sheets',
      type: 'Item',
      uom: 'EA',
      unitPrice: 12.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-002',
      name: 'Ballpoint Pens - Blue',
      description: 'Pack of 10 blue ballpoint pens',
      type: 'Item',
      uom: 'EA',
      unitPrice: 5.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-003',
      name: 'Office Chair - Ergonomic',
      description: 'Ergonomic office chair with lumbar support',
      type: 'Item',
      uom: 'EA',
      unitPrice: 249.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-004',
      name: 'Desk Organizer',
      description: 'Desktop organizer with multiple compartments',
      type: 'Item',
      uom: 'EA',
      unitPrice: 19.99,
      taxCode: 'STD',
      taxRate: 10
    }],
    V002: [{
      id: 'CAT002-001',
      name: 'Laptop - Business Series',
      description: 'Business laptop with 16GB RAM, 512GB SSD',
      type: 'Item',
      uom: 'EA',
      unitPrice: 1299.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-002',
      name: 'Desktop Computer - Standard',
      description: 'Standard desktop computer for office use',
      type: 'Item',
      uom: 'EA',
      unitPrice: 899.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-003',
      name: 'Monitor - 24 inch',
      description: '24-inch LED monitor, Full HD',
      type: 'Item',
      uom: 'EA',
      unitPrice: 199.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-004',
      name: 'IT Support - Basic',
      description: 'Basic IT support package, 10 hours',
      type: 'Service',
      uom: 'HR',
      unitPrice: 75.0,
      taxCode: 'STD',
      taxRate: 10
    }],
    V004: [{
      id: 'CAT004-001',
      name: 'Server - Entry Level',
      description: 'Entry level server for small businesses',
      type: 'Item',
      uom: 'EA',
      unitPrice: 1499.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT004-002',
      name: 'Server - Enterprise',
      description: 'Enterprise grade server with high performance',
      type: 'Item',
      uom: 'EA',
      unitPrice: 3999.99,
      taxCode: 'STD',
      taxRate: 10
    }]
  });
  // Sample addresses for different contexts
  const [addresses, setAddresses] = useState({
    tenant: [{
      id: 'TA001',
      name: 'Corporate HQ',
      street: '123 Main St',
      city: 'New York',
      state: 'NY',
      postalCode: '10001',
      country: 'United States'
    }, {
      id: 'TA002',
      name: 'Distribution Center',
      street: '456 Warehouse Blvd',
      city: 'Chicago',
      state: 'IL',
      postalCode: '60007',
      country: 'United States'
    }],
    entity: [{
      id: 'EA001',
      name: 'North America Office',
      street: '789 Business Park',
      city: 'Boston',
      state: 'MA',
      postalCode: '02108',
      country: 'United States'
    }, {
      id: 'EA002',
      name: 'European Office',
      street: '10 Tech Square',
      city: 'London',
      postalCode: 'EC1A 1BB',
      country: 'United Kingdom'
    }],
    site: [{
      id: 'SA001',
      name: 'Manufacturing Plant',
      street: '567 Industrial Way',
      city: 'Detroit',
      state: 'MI',
      postalCode: '48127',
      country: 'United States'
    }, {
      id: 'SA002',
      name: 'Research Lab',
      street: '890 Innovation Dr',
      city: 'San Francisco',
      state: 'CA',
      postalCode: '94107',
      country: 'United States'
    }]
  });
  const [showCustomAddress, setShowCustomAddress] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [filteredVendors, setFilteredVendors] = useState(vendors);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [currentLineItemIndex, setCurrentLineItemIndex] = useState(0);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [filteredCatalogItems, setFilteredCatalogItems] = useState([]);
  // Unit of measure options
  const uomOptions = ['EA', 'KG', 'LB', 'M', 'CM', 'L', 'ML', 'HR', 'DAY', 'MONTH', 'YEAR'];
  // Tax codes with descriptions and rates
  const taxCodes = [{
    code: 'STD',
    name: 'Standard Rate',
    rate: 10
  }, {
    code: 'RED',
    name: 'Reduced Rate',
    rate: 5
  }, {
    code: 'ZERO',
    name: 'Zero Rate',
    rate: 0
  }, {
    code: 'EXM',
    name: 'Exempt',
    rate: 0
  }];
  // Urgency levels
  const urgencyLevels = [{
    value: 'low',
    label: 'Low - No specific deadline'
  }, {
    value: 'normal',
    label: 'Normal - Required in standard timeframe'
  }, {
    value: 'high',
    label: 'High - Required soon'
  }, {
    value: 'critical',
    label: 'Critical - Urgent requirement'
  }];
  useEffect(() => {
    // Filter vendors based on search query
    if (vendorSearchQuery) {
      const filtered = vendors.filter(vendor => vendor.name.toLowerCase().includes(vendorSearchQuery.toLowerCase()) || vendor.id.toLowerCase().includes(vendorSearchQuery.toLowerCase()));
      setFilteredVendors(filtered);
    } else {
      setFilteredVendors(vendors);
    }
  }, [vendorSearchQuery, vendors]);
  useEffect(() => {
    // Filter catalog items based on search query
    if (showCatalogModal && formData.vendorId) {
      const catalog = vendorCatalogs[formData.vendorId] || [];
      if (catalogSearchQuery) {
        const filtered = catalog.filter(item => item.name.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || item.description.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || item.id.toLowerCase().includes(catalogSearchQuery.toLowerCase()));
        setFilteredCatalogItems(filtered);
      } else {
        setFilteredCatalogItems(catalog);
      }
    }
  }, [catalogSearchQuery, formData.vendorId, showCatalogModal, vendorCatalogs]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    if (field === 'taxCode') {
      // Find the tax rate for the selected tax code
      const selectedTaxCode = taxCodes.find(tax => tax.code === value);
      const taxRate = selectedTaxCode ? selectedTaxCode.rate : 0;
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        taxRate: taxRate
      };
      // Recalculate tax amount
      const subtotal = updatedItems[index].quantity * updatedItems[index].unitPrice;
      updatedItems[index].taxAmount = subtotal * taxRate / 100;
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: field === 'quantity' || field === 'unitPrice' ? parseFloat(value) || 0 : value
      };
      // Recalculate tax amount if quantity or unit price changes
      if (field === 'quantity' || field === 'unitPrice') {
        const subtotal = updatedItems[index].quantity * updatedItems[index].unitPrice;
        updatedItems[index].taxAmount = subtotal * updatedItems[index].taxRate / 100;
      }
    }
    setFormData({
      ...formData,
      items: updatedItems
    });
  };
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        description: '',
        quantity: 1,
        unitPrice: 0,
        type: 'Item',
        uom: 'EA',
        taxCode: 'STD',
        taxRate: 10,
        taxAmount: 0,
        catalogItemId: '',
        glCode: '',
        itemCode: ''
      }]
    });
  };
  const removeItem = index => {
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    setFormData({
      ...formData,
      items: updatedItems
    });
  };
  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };
  const calculateTaxTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.taxAmount, 0);
  };
  const calculateTotal = () => {
    return calculateSubtotal() + calculateTaxTotal();
  };
  const handleVendorSelect = vendor => {
    setFormData({
      ...formData,
      vendor: vendor.name,
      vendorId: vendor.id
    });
    setVendorSearchQuery('');
  };
  const handleAddressTypeChange = e => {
    const addressType = e.target.value;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        type: addressType,
        addressId: ''
      }
    });
    setShowCustomAddress(addressType === 'custom');
  };
  const handleAddressSelect = e => {
    const addressId = e.target.value;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        addressId: addressId
      }
    });
  };
  const handleCustomAddressChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        customAddress: {
          ...formData.deliveryAddress.customAddress,
          [name.replace('customAddress_', '')]: value
        }
      }
    });
  };
  const openCatalogModal = index => {
    setCurrentLineItemIndex(index);
    setCatalogSearchQuery('');
    // Reset filtered catalog items to show all items for the vendor
    if (formData.vendorId && vendorCatalogs[formData.vendorId]) {
      setFilteredCatalogItems(vendorCatalogs[formData.vendorId]);
    } else {
      setFilteredCatalogItems([]);
    }
    setShowCatalogModal(true);
  };
  const closeCatalogModal = () => {
    setShowCatalogModal(false);
    setCatalogSearchQuery('');
  };
  const handleCatalogItemSelect = catalogItem => {
    const updatedItems = [...formData.items];
    // Calculate tax amount
    const taxAmount = catalogItem.unitPrice * catalogItem.taxRate / 100;
    // Update the line item with catalog item details
    updatedItems[currentLineItemIndex] = {
      description: catalogItem.description,
      quantity: 1,
      unitPrice: catalogItem.unitPrice,
      type: catalogItem.type,
      uom: catalogItem.uom,
      taxCode: catalogItem.taxCode,
      taxRate: catalogItem.taxRate,
      taxAmount: taxAmount,
      catalogItemId: catalogItem.id,
      itemCode: catalogItem.id // Set the itemCode to the catalog item's ID
    };
    setFormData({
      ...formData,
      items: updatedItems
    });
    closeCatalogModal();
  };
  // Check if the selected vendor has a catalog
  const selectedVendorHasCatalog = () => {
    if (!formData.vendorId) return false;
    const vendor = vendors.find(v => v.id === formData.vendorId);
    return vendor ? vendor.hasCatalog : false;
  };
  const handleFileUpload = filesWithTypes => {
    // Convert the file objects to a format that can be stored in state
    const newDocuments = filesWithTypes.map(({
      file,
      documentType
    }) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      documentType: documentType,
      file: file,
      uploadDate: new Date().toISOString()
    }));
    setFormData({
      ...formData,
      documents: [...formData.documents, ...newDocuments]
    });
  };
  const removeDocument = index => {
    const updatedDocuments = [...formData.documents];
    updatedDocuments.splice(index, 1);
    setFormData({
      ...formData,
      documents: updatedDocuments
    });
  };
  const updateDocumentType = (index, newType) => {
    const updatedDocuments = [...formData.documents];
    updatedDocuments[index] = {
      ...updatedDocuments[index],
      documentType: newType
    };
    setFormData({
      ...formData,
      documents: updatedDocuments
    });
  };
  const handleSubmit = (e, isDraft = true) => {
    e.preventDefault();
    // Validate line items if not draft
    if (!isDraft) {
      // Validate line items
      const lineItemErrors = formData.items.map(item => {
        const itemErrors = {};
        if (!item.description) {
          itemErrors.description = 'Description is required';
        }
        if (!item.quantity || item.quantity <= 0) {
          itemErrors.quantity = 'Quantity must be greater than 0';
        }
        return Object.keys(itemErrors).length > 0 ? itemErrors : null;
      });
      // Update errors state
      setErrors({
        lineItems: lineItemErrors
      });
      // Only save if there are no errors
      if (lineItemErrors.some(error => error !== null)) {
        return;
      }
    }
    onSave({
      ...formData,
      total: calculateTotal()
    }, isDraft);
  };
  return <form onSubmit={e => handleSubmit(e, false)} data-id="element-14876">
      <div className="shadow sm:rounded-md sm:overflow-hidden" data-id="element-14877">
        <div className="px-4 py-5 bg-white space-y-6 sm:p-6" data-id="element-14878">
          <div className="grid grid-cols-6 gap-6" data-id="element-14879">
            <div className="col-span-6 sm:col-span-3" data-id="element-14880">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-14881">
                Requisition Number
              </label>
              <input type="text" name="name" id="name" value={formData.name} disabled className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 bg-gray-50 rounded-md cursor-not-allowed" data-id="element-14882" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-14883">
                System generated reference number
              </p>
            </div>
            {/* Vendor Lookup */}
            <div className="col-span-6 sm:col-span-3" data-id="element-14884">
              <label htmlFor="vendor" className="block text-sm font-medium text-gray-700" data-id="element-14885">
                Vendor
              </label>
              <div className="mt-1 relative" data-id="element-14886">
                <div className="flex" data-id="element-14887">
                  <div className="relative flex-grow" data-id="element-14888">
                    <input type="text" id="vendorSearch" value={vendorSearchQuery} onChange={e => setVendorSearchQuery(e.target.value)} placeholder="Search vendors..." className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14889" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-14890">
                      <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-14891" />
                    </div>
                  </div>
                </div>
                {/* Selected vendor display */}
                {formData.vendor && <div className="mt-2 flex items-center p-2 bg-gray-50 border border-gray-200 rounded-md" data-id="element-14892">
                    <span className="text-sm font-medium text-gray-700" data-id="element-14893">
                      {formData.vendor}
                    </span>
                    <span className="ml-2 text-xs text-gray-500" data-id="element-14894">
                      ({formData.vendorId})
                    </span>
                    {selectedVendorHasCatalog() && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-14895">
                        <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-14896" />
                        Catalog Available
                      </span>}
                  </div>}
                {/* Vendor search results */}
                {vendorSearchQuery && <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md overflow-hidden" data-id="element-14897">
                    <ul className="max-h-60 overflow-auto" data-id="element-14898">
                      {filteredVendors.length > 0 ? filteredVendors.map(vendor => <li key={vendor.id} className="cursor-pointer p-2 hover:bg-gray-50" onClick={() => handleVendorSelect(vendor)} data-id="element-14899">
                            <div className="flex justify-between" data-id="element-14900">
                              <span className="font-medium" data-id="element-14901">{vendor.name}</span>
                              <span className="text-xs text-gray-500" data-id="element-14902">
                                {vendor.id}
                              </span>
                            </div>
                            <div className="flex items-center" data-id="element-14903">
                              <span className="text-xs text-gray-500" data-id="element-14904">
                                {vendor.country}
                              </span>
                              {vendor.hasCatalog && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-14905">
                                  <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-14906" />
                                  Catalog
                                </span>}
                            </div>
                          </li>) : <li className="p-2 text-center text-sm text-gray-500" data-id="element-14907">
                          No vendors found
                        </li>}
                    </ul>
                  </div>}
              </div>
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-14908">
              <label htmlFor="deliveryDate" className="block text-sm font-medium text-gray-700" data-id="element-14909">
                Required Delivery Date
              </label>
              <input type="date" name="deliveryDate" id="deliveryDate" value={formData.deliveryDate} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14910" />
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-14911">
              <label htmlFor="urgencyLevel" className="block text-sm font-medium text-gray-700" data-id="element-14912">
                Urgency Level
              </label>
              <select id="urgencyLevel" name="urgencyLevel" value={formData.urgencyLevel} onChange={handleChange} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-14913">
                {urgencyLevels.map(level => <option key={level.value} value={level.value} data-id="element-14914">
                    {level.label}
                  </option>)}
              </select>
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-14915">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-14916">
                Currency
              </label>
              <div className="mt-1 relative" data-id="element-14917">
                <select id="currency" name="currency" value={formData.currency} onChange={handleChange} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-14918">
                  <option value="AUD" data-id="element-14919">AUD - Australian Dollar</option>
                  <option value="USD" data-id="element-14920">USD - US Dollar</option>
                  <option value="EUR" data-id="element-14921">EUR - Euro</option>
                  <option value="GBP" data-id="element-14922">GBP - British Pound</option>
                  <option value="JPY" data-id="element-14923">JPY - Japanese Yen</option>
                  <option value="CAD" data-id="element-14924">CAD - Canadian Dollar</option>
                  <option value="INR" data-id="element-14925">INR - Indian Rupee</option>
                </select>
                <div className="mt-1 text-xs text-gray-500 flex items-center" data-id="element-14926">
                  <InfoIcon className="h-3 w-3 mr-1 text-blue-500" data-id="element-14927" />
                  Using system default currency
                </div>
              </div>
            </div>
            {/* Purchase Justification Section */}
            <div className="col-span-6" data-id="element-14928">
              <fieldset className="border border-gray-200 rounded-md p-4" data-id="element-14929">
                <legend className="text-sm font-medium text-gray-700 px-2" data-id="element-14930">
                  Purchase Justification
                </legend>
                <div className="mb-4" data-id="element-14931">
                  <label htmlFor="purchaseJustification" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14932">
                    Why is this purchase needed? *
                  </label>
                  <textarea id="purchaseJustification" name="purchaseJustification" rows={3} value={formData.purchaseJustification} onChange={handleChange} placeholder="Explain the business need for this purchase" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-14933"></textarea>
                </div>
                <div className="mb-4" data-id="element-14934">
                  <label htmlFor="businessCase" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14935">
                    Business Case
                  </label>
                  <textarea id="businessCase" name="businessCase" rows={3} value={formData.businessCase} onChange={handleChange} placeholder="Provide any ROI calculations or business case details" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-14936"></textarea>
                </div>
                <div data-id="element-14937">
                  <label htmlFor="vendorJustification" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14938">
                    Why this vendor? *
                  </label>
                  <textarea id="vendorJustification" name="vendorJustification" rows={3} value={formData.vendorJustification} onChange={handleChange} placeholder="Explain why this vendor is recommended (pricing, quality, service, etc.)" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-14939"></textarea>
                </div>
              </fieldset>
            </div>
            {/* Delivery Address Section */}
            <div className="col-span-6" data-id="element-14940">
              <fieldset className="border border-gray-200 rounded-md p-4" data-id="element-14941">
                <legend className="text-sm font-medium text-gray-700 px-2" data-id="element-14942">
                  Delivery Address
                </legend>
                <div className="mb-4" data-id="element-14943">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14944">
                    Address Type
                  </label>
                  <div className="flex space-x-4" data-id="element-14945">
                    <div className="flex items-center" data-id="element-14946">
                      <input id="addressTypeTenant" name="addressType" type="radio" value="tenant" checked={formData.deliveryAddress.type === 'tenant'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-14947" />
                      <label htmlFor="addressTypeTenant" className="ml-2 block text-sm text-gray-700" data-id="element-14948">
                        Tenant Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-14949">
                      <input id="addressTypeEntity" name="addressType" type="radio" value="entity" checked={formData.deliveryAddress.type === 'entity'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-14950" />
                      <label htmlFor="addressTypeEntity" className="ml-2 block text-sm text-gray-700" data-id="element-14951">
                        Entity Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-14952">
                      <input id="addressTypeSite" name="addressType" type="radio" value="site" checked={formData.deliveryAddress.type === 'site'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-14953" />
                      <label htmlFor="addressTypeSite" className="ml-2 block text-sm text-gray-700" data-id="element-14954">
                        Site Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-14955">
                      <input id="addressTypeCustom" name="addressType" type="radio" value="custom" checked={formData.deliveryAddress.type === 'custom'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-14956" />
                      <label htmlFor="addressTypeCustom" className="ml-2 block text-sm text-gray-700" data-id="element-14957">
                        Custom Address
                      </label>
                    </div>
                  </div>
                </div>
                {formData.deliveryAddress.type !== 'custom' && <div className="mb-4" data-id="element-14958">
                    <label htmlFor="addressSelect" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14959">
                      Select Address
                    </label>
                    <select id="addressSelect" value={formData.deliveryAddress.addressId} onChange={handleAddressSelect} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-14960">
                      <option value="" data-id="element-14961">Select an address</option>
                      {addresses[formData.deliveryAddress.type].map(address => <option key={address.id} value={address.id} data-id="element-14962">
                            {address.name} - {address.street}, {address.city}
                          </option>)}
                    </select>
                    {/* Display selected address details */}
                    {formData.deliveryAddress.addressId && <div className="mt-3 p-3 bg-gray-50 rounded-md" data-id="element-14963">
                        {(() => {
                    const selectedAddress = addresses[formData.deliveryAddress.type].find(addr => addr.id === formData.deliveryAddress.addressId);
                    if (selectedAddress) {
                      return <div className="flex items-start" data-id="element-14964">
                                <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-2" data-id="element-14965" />
                                <div data-id="element-14966">
                                  <p className="text-sm font-medium" data-id="element-14967">
                                    {selectedAddress.name}
                                  </p>
                                  <p className="text-sm text-gray-500" data-id="element-14968">
                                    {selectedAddress.street},{' '}
                                    {selectedAddress.city},
                                    {selectedAddress.state && ` ${selectedAddress.state},`}{' '}
                                    {selectedAddress.postalCode}
                                  </p>
                                  <p className="text-sm text-gray-500" data-id="element-14969">
                                    {selectedAddress.country}
                                  </p>
                                </div>
                              </div>;
                    }
                    return null;
                  })()}
                      </div>}
                  </div>}
                {formData.deliveryAddress.type === 'custom' && <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-14970">
                    <div className="sm:col-span-6" data-id="element-14971">
                      <label htmlFor="customAddress_name" className="block text-sm font-medium text-gray-700" data-id="element-14972">
                        Address Name/Label
                      </label>
                      <input type="text" name="customAddress_name" id="customAddress_name" value={formData.deliveryAddress.customAddress.name} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="e.g., Warehouse, Client Site" data-id="element-14973" />
                    </div>
                    <div className="sm:col-span-6" data-id="element-14974">
                      <label htmlFor="customAddress_street" className="block text-sm font-medium text-gray-700" data-id="element-14975">
                        Street Address
                      </label>
                      <input type="text" name="customAddress_street" id="customAddress_street" value={formData.deliveryAddress.customAddress.street} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14976" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14977">
                      <label htmlFor="customAddress_city" className="block text-sm font-medium text-gray-700" data-id="element-14978">
                        City
                      </label>
                      <input type="text" name="customAddress_city" id="customAddress_city" value={formData.deliveryAddress.customAddress.city} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14979" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14980">
                      <label htmlFor="customAddress_state" className="block text-sm font-medium text-gray-700" data-id="element-14981">
                        State / Province
                      </label>
                      <input type="text" name="customAddress_state" id="customAddress_state" value={formData.deliveryAddress.customAddress.state} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14982" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14983">
                      <label htmlFor="customAddress_postalCode" className="block text-sm font-medium text-gray-700" data-id="element-14984">
                        Postal Code
                      </label>
                      <input type="text" name="customAddress_postalCode" id="customAddress_postalCode" value={formData.deliveryAddress.customAddress.postalCode} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14985" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14986">
                      <label htmlFor="customAddress_country" className="block text-sm font-medium text-gray-700" data-id="element-14987">
                        Country
                      </label>
                      <input type="text" name="customAddress_country" id="customAddress_country" value={formData.deliveryAddress.customAddress.country} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14988" />
                    </div>
                  </div>}
              </fieldset>
            </div>
          </div>
          <div data-id="element-14989">
            <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-14990">
              Line Items
            </h3>
            <div className="mt-4 space-y-4" data-id="element-14991">
              {formData.items.map((item, index) => <div key={index} className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-14992">
                  <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-b border-gray-200" data-id="element-14993">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-14994">
                      Item {index + 1}
                    </h4>
                    <button type="button" onClick={() => removeItem(index)} className="text-red-600 hover:text-red-900" data-id="element-14995">
                      <TrashIcon className="h-5 w-5" data-id="element-14996" />
                    </button>
                  </div>
                  {item.catalogItemId && <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md" data-id="element-14997">
                      <div className="flex items-center" data-id="element-14998">
                        <BookOpenIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-14999" />
                        <span className="text-sm font-medium text-blue-700" data-id="element-15000">
                          Catalog Item: {item.catalogItemId}
                        </span>
                      </div>
                    </div>}
                  <div className="p-4" data-id="element-15001">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-6" data-id="element-15002">
                      <div className="sm:col-span-3" data-id="element-15003">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15004">
                          Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.type} onChange={e => handleItemChange(index, 'type', e.target.value)} data-id="element-15005">
                          <option value="Item" data-id="element-15006">Item</option>
                          <option value="Service" data-id="element-15007">Service</option>
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-15008">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15009">
                          Item Code
                        </label>
                        <div className="flex" data-id="element-15010">
                          <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.itemCode || ''} onChange={e => handleItemChange(index, 'itemCode', e.target.value)} placeholder="Item code" data-id="element-15011" />
                          <button type="button" onClick={() => openCatalogModal(index)} className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15012">
                            Select from Catalog
                          </button>
                        </div>
                      </div>
                      <div className="sm:col-span-6" data-id="element-15013">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15014">
                          Description <span className="text-red-500" data-id="element-15015">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.description ? 'border-red-300' : ''}`} value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} placeholder="Brief description" data-id="element-15016" />
                        {errors.lineItems && errors.lineItems[index]?.description && <p className="mt-1 text-sm text-red-600" data-id="element-15017">
                              {errors.lineItems[index].description}
                            </p>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-15018">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15019">
                          Quantity <span className="text-red-500" data-id="element-15020">*</span>
                        </label>
                        <input type="number" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.quantity ? 'border-red-300' : ''}`} value={item.quantity} onChange={e => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} min="0" step="1" data-id="element-15021" />
                        {errors.lineItems && errors.lineItems[index]?.quantity && <p className="mt-1 text-sm text-red-600" data-id="element-15022">
                              {errors.lineItems[index].quantity}
                            </p>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-15023">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15024">
                          Unit of Measure
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.uom} onChange={e => handleItemChange(index, 'uom', e.target.value)} data-id="element-15025">
                          {uomOptions.map(uom => <option key={uom} value={uom} data-id="element-15026">
                              {uom}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-15027">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15028">
                          Unit Price <span className="text-red-500" data-id="element-15029">*</span>
                        </label>
                        <input type="number" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.unitPrice} onChange={e => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)} min="0" step="0.01" data-id="element-15030" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-15031">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15032">
                          Tax Code
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.taxCode} onChange={e => handleItemChange(index, 'taxCode', e.target.value)} data-id="element-15033">
                          {taxCodes.map(code => <option key={code.code} value={code.code} data-id="element-15034">
                              {code.code} - {code.name}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-15035">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15036">
                          Tax Amount
                        </label>
                        <div className="p-2 bg-gray-50 rounded-md border border-gray-300 text-sm font-medium" data-id="element-15037">
                          {formatCurrency(item.taxAmount || 0, formData.currency || 'USD', locale)}
                        </div>
                      </div>
                      <div className="sm:col-span-2" data-id="element-15038">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15039">
                          GL Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.glCode || ''} onChange={e => handleItemChange(index, 'glCode', e.target.value)} placeholder="GL account code" data-id="element-15040" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-15041">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15042">
                          Line Total
                        </label>
                        <div className="p-2 bg-gray-50 rounded-md border border-gray-300 text-lg font-medium" data-id="element-15043">
                          {formatCurrency(item.quantity * item.unitPrice, formData.currency || 'USD', locale)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
              <div className="mt-2" data-id="element-15044">
                <button type="button" onClick={addItem} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15045">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-15046" />
                  Add Line Item
                </button>
              </div>
            </div>
          </div>
          {/* Upload Documents Section */}
          <div data-id="element-15047">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4" data-id="element-15048">
              Supporting Documents
            </h3>
            <FileUpload onFileUpload={handleFileUpload} documentTypes={documentTypes} data-id="element-15049" />
            {formData.documents.length > 0 && <div className="mt-4" data-id="element-15050">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-15051">
                  Uploaded Documents
                </h4>
                <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-15052">
                  <ul className="divide-y divide-gray-200" data-id="element-15053">
                    {formData.documents.map((doc, index) => <li key={index} className="px-4 py-3 flex items-center justify-between" data-id="element-15054">
                        <div className="flex items-center" data-id="element-15055">
                          <FileIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-15056" />
                          <div data-id="element-15057">
                            <p className="text-sm font-medium text-gray-900" data-id="element-15058">
                              {doc.name}
                            </p>
                            <div className="flex items-center mt-1" data-id="element-15059">
                              <div className="mr-2" data-id="element-15060">
                                <select value={doc.documentType} onChange={e => updateDocumentType(index, e.target.value)} className="text-xs border-gray-300 rounded-md py-1 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" data-id="element-15061">
                                  {documentTypes.map(type => <option key={type} value={type} data-id="element-15062">
                                      {type}
                                    </option>)}
                                </select>
                              </div>
                              <span className="text-xs text-gray-500" data-id="element-15063">
                                {(doc.size / 1024).toFixed(1)} KB
                              </span>
                              <span className="text-xs text-gray-500 ml-2" data-id="element-15064">
                                Uploaded:{' '}
                                {new Date(doc.uploadDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button type="button" onClick={() => removeDocument(index)} className="text-red-600 hover:text-red-900" data-id="element-15065">
                          <TrashIcon className="h-5 w-5" data-id="element-15066" />
                        </button>
                      </li>)}
                  </ul>
                </div>
              </div>}
          </div>
          <div data-id="element-15067">
            <StandardizedNotes value={formData.notes} onChange={value => setFormData({
            ...formData,
            notes: value
          })} visibility={formData.noteVisibility} onVisibilityChange={visibility => setFormData({
            ...formData,
            noteVisibility: visibility
          })} visibilityDetails={formData.noteVisibilityDetails} onVisibilityDetailsChange={details => setFormData({
            ...formData,
            noteVisibilityDetails: details
          })} label="Additional Notes" placeholder="Add any additional notes or special instructions. Select who can view these notes using the visibility options." data-id="element-15068" />
          </div>
          <div className="border-t border-gray-200 pt-4" data-id="element-15069">
            <div className="flex justify-end" data-id="element-15070">
              <div className="w-64 bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-15071">
                <div className="flex justify-between items-center text-sm" data-id="element-15072">
                  <span className="text-gray-700" data-id="element-15073">Subtotal:</span>
                  <span className="font-medium" data-id="element-15074">
                    {formatCurrency(calculateSubtotal(), formData.currency, locale)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2" data-id="element-15075">
                  <span className="text-gray-700" data-id="element-15076">Tax:</span>
                  <span className="font-medium" data-id="element-15077">
                    {formatCurrency(calculateTaxTotal(), formData.currency, locale)}
                  </span>
                </div>
                <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between items-center" data-id="element-15078">
                  <span className="text-gray-900 font-medium" data-id="element-15079">Total:</span>
                  <span className="text-lg font-bold" data-id="element-15080">
                    {formatCurrency(calculateTotal(), formData.currency, locale)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 space-x-3" data-id="element-15081">
          <button type="button" onClick={e => handleSubmit(e, true)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15082">
            Save as Draft
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15083">
            Submit for Approval
          </button>
        </div>
      </div>
      {/* Catalog Item Selection Modal */}
      {showCatalogModal && <div className="fixed z-50 inset-0 overflow-y-auto" data-id="element-15084">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-15085">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={closeCatalogModal} data-id="element-15086">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-15087"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-15088">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-15089">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-15090">
                <div className="sm:flex sm:items-start" data-id="element-15091">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-15092">
                    <div className="flex justify-between items-center mb-4" data-id="element-15093">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15094">
                        Select Item from Catalog
                      </h3>
                      <button type="button" onClick={closeCatalogModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-15095">
                        <XIcon className="h-6 w-6" data-id="element-15096" />
                      </button>
                    </div>
                    <div className="mb-4" data-id="element-15097">
                      <div className="relative rounded-md shadow-sm" data-id="element-15098">
                        <input type="text" value={catalogSearchQuery} onChange={e => setCatalogSearchQuery(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search catalog items..." data-id="element-15099" />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-15100">
                          <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-15101" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 max-h-96 overflow-y-auto" data-id="element-15102">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-15103">
                        <thead className="bg-gray-50" data-id="element-15104">
                          <tr data-id="element-15105">
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15106">
                              Item Code
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15107">
                              Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15108">
                              Type
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15109">
                              UoM
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15110">
                              Price
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15111">
                              Tax
                            </th>
                            <th scope="col" className="relative px-6 py-3" data-id="element-15112">
                              <span className="sr-only" data-id="element-15113">Select</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-15114">
                          {filteredCatalogItems.length > 0 ? filteredCatalogItems.map(catalogItem => <tr key={catalogItem.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleCatalogItemSelect(catalogItem)} data-id="element-15115">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15116">
                                  {catalogItem.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15117">
                                  <div data-id="element-15118">
                                    <div className="font-medium text-gray-900" data-id="element-15119">
                                      {catalogItem.name}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-15120">
                                      {catalogItem.description}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15121">
                                  {catalogItem.type}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15122">
                                  {catalogItem.uom}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15123">
                                  {formatCurrency(catalogItem.unitPrice, formData.currency, locale)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15124">
                                  {catalogItem.taxCode} ({catalogItem.taxRate}%)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-15125">
                                  <button type="button" onClick={e => {
                            e.stopPropagation();
                            handleCatalogItemSelect(catalogItem);
                          }} className="text-indigo-600 hover:text-indigo-900" data-id="element-15126">
                                    Select
                                  </button>
                                </td>
                              </tr>) : <tr data-id="element-15127">
                              <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-15128">
                                {formData.vendorId && vendorCatalogs[formData.vendorId] ? 'No matching catalog items found' : 'No catalog items available for this vendor'}
                              </td>
                            </tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-15129">
                <button type="button" onClick={closeCatalogModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-15130">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </form>;
}